import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import defaultAlbumThumbnail from '../../assets/default_album_thumbnail.svg';
import theme from '../../theme';
import ArtistLink from '../common/ArtistLink';
import AlbumLink from '../common/AlbumLink';
import AlbumTitleLink from '../common/AlbumTitleLink';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { useUserStore } from '../../stores/userStore';
import { trackListContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, LIST_ACTION_TYPE } from '../../constants/gaEnums';

const PlaylistTrackCard = ({
  albumImageUrl,
  trackTitle,
  artistName,
  albumTitle,
  releaseDate,
  contentRating = 'CLEAN',
  rank,
  artistSource,
  artistSourceId,
  artistSlug,
  artistDbId,
  albumId,
  albumSourceId,
  albumSource,
  albumSlug,
  referrerSection,
}) => {
  const [imageSrc, setImageSrc] = useState(albumImageUrl || defaultAlbumThumbnail);
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const albumUrl = albumSlug
    ? `/reviews/${albumSlug}`
    : albumSourceId
      ? `/reviews/${albumSource?.toLowerCase()}/${albumSourceId}`
      : `/reviews/local/${albumId}`;
  const albumLinkState = referrerSection ? { referrerSection } : undefined;

  useEffect(() => {
    setImageSrc(albumImageUrl || defaultAlbumThumbnail);
  }, [albumImageUrl]);

  const handleError = () => {
    setImageSrc(defaultAlbumThumbnail);
  };

  const trackAlbumClick = () => {
    trackListContentClick(
      {
        contentType: CONTENT_TYPE.LIST_SONG,
        actionType: LIST_ACTION_TYPE.ALBUM_CLICK,
        contentId: `album_${albumId || albumSourceId}`,
        contentName: albumTitle,
      },
      user,
    );
  };

  const handleAlbumClick = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      trackAlbumClick();
      navigateWithScrollMemory(albumUrl, albumLinkState ? { state: albumLinkState } : undefined);
    }
  };

  const handleArtistClick = () => {
    trackListContentClick(
      {
        contentType: CONTENT_TYPE.LIST_SONG,
        actionType: LIST_ACTION_TYPE.ARTIST_CLICK,
        contentId: `artist_${artistSourceId}`,
        contentName: artistName,
      },
      user,
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 2 },
        width: '100%',
        px: { xs: 2, md: 2, lg: 2.5 },
        py: { xs: 2, md: 2.5, lg: 2.5 },
        bgcolor: theme.palette.grey.bg,
      }}
    >
      {/* 1. 순서 */}
      {rank && (
        <Typography
          sx={{
            width: { xs: 10, md: 20 },
            textAlign: 'left',
            flexShrink: 0,
            fontWeight: 'bold',
            fontSize: { xs: 12, md: 13 },
          }}
        >
          {rank}
        </Typography>
      )}

      {/* 2. 이미지 + 정보 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, md: 2 },
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* 이미지 */}
        <Link to={albumUrl} onClick={handleAlbumClick} style={{ display: 'block', flexShrink: 0 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={trackTitle}
            onError={handleError}
            sx={{
              width: { xs: 64, md: 80 },
              height: { xs: 64, md: 80 },
              display: 'block',
            }}
          />
        </Link>

        {/* 정보 */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* 제목 */}
          <AlbumTitleLink
            title={trackTitle}
            contentRating={contentRating}
            sx={{
              fontSize: { xs: 13, md: 14 },
            }}
            iconSx={{
              width: { xs: 12, md: 14 },
              height: { xs: 12, md: 14 },
            }}
          />

          {/* 아티스트 */}
          {artistName && (
            <ArtistLink
              artistName={artistName}
              artistSource={artistSource}
              artistSourceId={artistSourceId}
              artistSlug={artistSlug}
              artistDbId={artistDbId}
              onClick={handleArtistClick}
              sx={{
                mt: 1,
                fontSize: { xs: 12, md: 14 },
                color: theme.palette.black[4],
                WebkitLineClamp: 2,
              }}
            />
          )}

          {/* 앨범명 */}
          {albumTitle && (
            <AlbumLink
              albumTitle={albumTitle}
              albumId={albumId}
              albumSourceId={albumSourceId}
              albumSource={albumSource}
              albumSlug={albumSlug}
              onClick={trackAlbumClick}
              sx={{
                mt: 1,
                fontSize: { xs: 12, md: 13 },
                color: theme.palette.black[4],
                WebkitLineClamp: 2,
              }}
            />
          )}

          {/* 발매일 */}
          {releaseDate && (
            <Typography
              sx={{
                mt: { xs: 1.5, md: 2 },
                fontSize: { xs: 12, md: 14 },
                color: theme.palette.black[4],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 'normal',
              }}
              title={releaseDate}
            >
              {releaseDate}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlaylistTrackCard;
